import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { supabase, getCurrentUser } from '../lib/supabase';
import { IconCheckCircle, IconRing, IconWatch, IconDna, IconClipboardCheck, IconSettings } from './Icons';

/* ── Types ─────────────────────────────────────────────────────────────────── */

interface ChecklistItem {
  id: string;
  label: string;
  description: string;
  done: boolean;
  href: string;
  icon: React.ReactNode;
}

/* ── Component ─────────────────────────────────────────────────────────────── */

export default function OnboardingChecklist() {
  const [items, setItems]     = useState<ChecklistItem[]>([]);
  const [loading, setLoading] = useState(true);
  const [dismissed, setDismissed] = useState(false);

  useEffect(() => {
    (async () => {
      // Check if user previously dismissed the checklist
      const dismissedKey = 'arete_onboarding_dismissed';
      if (localStorage.getItem(dismissedKey) === 'true') {
        setDismissed(true);
        setLoading(false);
        return;
      }

      const user = await getCurrentUser();
      if (!user) { setLoading(false); return; }

      // Check all data sources in parallel
      try {
        const [profileRes, appleRes, labsRes, checkinRes, ouraSyncRes] = await Promise.all([
          supabase.from('profiles').select('full_name, dob, oura_token').eq('id', user.id).maybeSingle(),
          supabase.from('apple_health_syncs').select('user_id').eq('user_id', user.id).maybeSingle(),
          supabase.from('labs').select('id').eq('user_id', user.id).limit(1).maybeSingle(),
          supabase.from('phq4').select('id').eq('user_id', user.id).limit(1).maybeSingle(),
          supabase.from('oura_sync_log').select('user_id').eq('user_id', user.id).maybeSingle(),
        ]);

        const profile = profileRes.data;
      const hasProfile  = !!(profile?.full_name && profile?.dob);
      const hasOura     = !!profile?.oura_token || !!ouraSyncRes.data;
      const hasApple    = !!appleRes.data;
      const hasLabs     = !!labsRes.data;
      const hasCheckin  = !!checkinRes.data;

      const checklist: ChecklistItem[] = [
        {
          id: 'profile',
          label: 'Complete your profile',
          description: 'Add your name and date of birth',
          done: hasProfile,
          href: '/settings',
          icon: <IconSettings size={16} />,
        },
        {
          id: 'oura',
          label: 'Connect Oura Ring',
          description: 'Sync sleep, HRV, readiness & stress',
          done: hasOura,
          href: '/connect',
          icon: <IconRing size={16} />,
        },
        {
          id: 'apple',
          label: 'Connect Apple Health',
          description: 'Sync steps, heart rate & workouts',
          done: hasApple,
          href: '/connect',
          icon: <IconWatch size={16} />,
        },
        {
          id: 'labs',
          label: 'Upload lab results',
          description: 'Upload a PDF or enter biomarkers manually',
          done: hasLabs,
          href: '/labs',
          icon: <IconDna size={16} />,
        },
        {
          id: 'checkin',
          label: 'Complete your first check-in',
          description: 'PHQ-4 screening + lifestyle pillars',
          done: hasCheckin,
          href: '/checkin',
          icon: <IconClipboardCheck size={16} />,
        },
      ];

      setItems(checklist);
      } catch (e) {
        console.warn('[OnboardingChecklist] load error:', e);
      }
      setLoading(false);
    })().catch(err => console.error('[OnboardingChecklist] unhandled error:', err));
  }, []);

  if (loading || dismissed) return null;

  const completedCount = items.filter(i => i.done).length;
  const allDone = completedCount === items.length;

  // Don't show if all complete
  if (allDone) return null;

  const progress = Math.round((completedCount / items.length) * 100);

  return (
    <div className="card border border-arete-blue/20 bg-gradient-to-br from-white to-blue-50/30">
      <div className="flex items-center justify-between mb-3">
        <div>
          <h3 className="text-base font-semibold text-arete-navy">Getting Started</h3>
          <p className="text-xs text-arete-muted mt-0.5">
            {completedCount} of {items.length} complete — connect your data sources to power your dashboard
          </p>
        </div>
        <button
          onClick={() => {
            localStorage.setItem('arete_onboarding_dismissed', 'true');
            setDismissed(true);
          }}
          className="text-xs text-arete-muted hover:text-arete-navy transition-colors"
          title="Dismiss"
        >
          &times;
        </button>
      </div>

      {/* Progress bar */}
      <div className="w-full bg-gray-200 rounded-full h-1.5 mb-4">
        <div
          className="bg-arete-blue h-1.5 rounded-full transition-all duration-500"
          style={{ width: `${progress}%` }}
        />
      </div>

      {/* Checklist */}
      <div className="space-y-1.5">
        {items.map(item => (
          <Link
            key={item.id}
            to={item.href}
            className={`flex items-center gap-3 rounded-xl px-3 py-2.5 transition-colors ${
              item.done
                ? 'bg-emerald-50/50'
                : 'bg-white hover:bg-gray-50 border border-gray-100'
            }`}
          >
            {item.done ? (
              <IconCheckCircle size={18} color="#22C55E" className="flex-shrink-0" />
            ) : (
              <div className="w-[18px] h-[18px] rounded-full border-2 border-gray-300 flex-shrink-0" />
            )}
            <div className="flex-1 min-w-0">
              <div className={`text-sm font-medium ${item.done ? 'text-emerald-700 line-through' : 'text-arete-navy'}`}>
                {item.label}
              </div>
              {!item.done && (
                <div className="text-[11px] text-arete-muted">{item.description}</div>
              )}
            </div>
            {!item.done && (
              <span className="text-arete-blue flex-shrink-0">
                {item.icon}
              </span>
            )}
          </Link>
        ))}
      </div>
    </div>
  );
}

/* ── Weekly Check-In Reminder ──────────────────────────────────────────────── */

export function CheckInReminder() {
  const [show, setShow]     = useState(false);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    (async () => {
      const user = await getCurrentUser();
      if (!user) { setLoading(false); return; }

      // Get current ISO week string
      const now = new Date();
      const oneJan = new Date(now.getFullYear(), 0, 1);
      const weekNum = Math.ceil(((now.getTime() - oneJan.getTime()) / 86400000 + oneJan.getDay() + 1) / 7);
      const weekStr = `${now.getFullYear()}-W${String(weekNum).padStart(2, '0')}`;

      // Check if there's a check-in for this week
      const { data } = await supabase
        .from('phq4')
        .select('id')
        .eq('user_id', user.id)
        .eq('week', weekStr)
        .maybeSingle();

      setShow(!data);
      setLoading(false);
    })().catch(err => console.error('[CheckInReminder] unhandled error:', err));
  }, []);

  if (loading || !show) return null;

  return (
    <Link
      to="/checkin"
      className="flex items-center gap-3 rounded-2xl bg-amber-50 border border-amber-200 px-4 py-3 hover:bg-amber-100/50 transition-colors"
    >
      <IconClipboardCheck size={20} color="#D97706" className="flex-shrink-0" />
      <div className="flex-1">
        <div className="text-sm font-medium text-amber-800">Weekly check-in available</div>
        <div className="text-xs text-amber-600">Complete your PHQ-4 screening and lifestyle pillars</div>
      </div>
      <span className="text-amber-500 text-sm font-medium">Go →</span>
    </Link>
  );
}

/* ── Sidebar-optimized Getting Started ──────────────────────────────────── */

export function SidebarOnboarding() {
  const [items, setItems] = useState<{ id: string; label: string; done: boolean; href: string }[]>([]);
  const [loading, setLoading] = useState(true);
  const [dismissed, setDismissed] = useState(false);

  useEffect(() => {
    async function loadChecklist() {
      if (localStorage.getItem('arete_onboarding_dismissed') === 'true') {
        setDismissed(true);
        setLoading(false);
        return;
      }

      const user = await getCurrentUser();
      if (!user) { setLoading(false); return; }

      try {
        const [profileRes, appleRes, labsRes, checkinRes, ouraSyncRes] = await Promise.all([
          supabase.from('profiles').select('full_name, dob, oura_token').eq('id', user.id).maybeSingle(),
          supabase.from('apple_health_syncs').select('user_id').eq('user_id', user.id).maybeSingle(),
          supabase.from('labs').select('id').eq('user_id', user.id).limit(1).maybeSingle(),
          supabase.from('phq4').select('id').eq('user_id', user.id).limit(1).maybeSingle(),
          supabase.from('oura_sync_log').select('user_id').eq('user_id', user.id).maybeSingle(),
        ]);

        const profile = profileRes.data;
        setItems([
          { id: 'profile', label: 'Complete profile', done: !!(profile?.full_name && profile?.dob), href: '/settings' },
          { id: 'oura', label: 'Connect Oura', done: !!profile?.oura_token || !!ouraSyncRes.data, href: '/connect' },
          { id: 'apple', label: 'Apple Health', done: !!appleRes.data, href: '/connect' },
          { id: 'labs', label: 'Upload labs', done: !!labsRes.data, href: '/labs' },
          { id: 'checkin', label: 'First check-in', done: !!checkinRes.data, href: '/checkin' },
        ]);
      } catch (e) {
        console.warn('[SidebarOnboarding] load error:', e);
      }
      setLoading(false);
    }

    loadChecklist();

    // Re-check when data sources change (Connect page dispatches this)
    const handleChange = () => loadChecklist();
    window.addEventListener('arete:datasource-change', handleChange);
    return () => window.removeEventListener('arete:datasource-change', handleChange);
  }, []);

  if (loading || dismissed) return null;

  const completedCount = items.filter(i => i.done).length;
  if (completedCount === items.length) return null;

  const progress = Math.round((completedCount / items.length) * 100);

  return (
    <div className="mt-4 mb-2">
      <div className="flex items-center justify-between px-3 mb-2">
        <span className="text-xs font-bold uppercase tracking-widest" style={{ color: 'rgba(47,74,52,0.45)' }}>Setup</span>
        <div className="flex items-center gap-2">
          <span className="text-[10px] font-medium" style={{ color: 'rgba(47,74,52,0.40)' }}>{completedCount}/{items.length}</span>
          <button
            onClick={() => { localStorage.setItem('arete_onboarding_dismissed', 'true'); setDismissed(true); }}
            className="text-sm leading-none transition-colors"
            style={{ color: 'rgba(47,74,52,0.25)' }}
            title="Dismiss"
          >×</button>
        </div>
      </div>

      {/* Progress bar */}
      <div className="px-3 mb-2">
        <div className="w-full rounded-full h-1" style={{ backgroundColor: 'rgba(47,74,52,0.10)' }}>
          <div
            className="bg-arete-teal h-1 rounded-full transition-all duration-500"
            style={{ width: `${progress}%` }}
          />
        </div>
      </div>

      {/* Checklist items */}
      <div className="space-y-0.5">
        {items.map(item => (
          <Link
            key={item.id}
            to={item.href}
            className="flex items-center gap-2.5 px-3 py-1.5 rounded-lg transition-colors group"
            style={{ }}
          >
            {item.done ? (
              <span className="w-4 h-4 rounded-full bg-arete-teal/70 flex items-center justify-center flex-shrink-0">
                <svg width={8} height={8} viewBox="0 0 12 12" fill="none" stroke="white" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                  <polyline points="2,6 5,9 10,3" />
                </svg>
              </span>
            ) : (
              <span className="w-4 h-4 rounded-full border-[1.5px] flex-shrink-0 transition-colors" style={{ borderColor: 'rgba(47,74,52,0.20)' }} />
            )}
            <span className={`text-xs truncate transition-colors ${item.done ? 'line-through' : ''}`}
              style={{ color: item.done ? 'rgba(47,74,52,0.45)' : 'rgba(47,74,52,0.70)' }}
            >
              {item.label}
            </span>
          </Link>
        ))}
      </div>
    </div>
  );
}

/* ── Hook: is weekly check-in due? ─────────────────────────────────────── */

export function useCheckInDue(): boolean {
  const [due, setDue] = useState(false);

  useEffect(() => {
    (async () => {
      const user = await getCurrentUser();
      if (!user) return;

      const sevenDaysAgo = new Date(Date.now() - 7 * 86400000).toISOString();

      const { data } = await supabase
        .from('phq4')
        .select('id')
        .eq('user_id', user.id)
        .gte('created_at', sevenDaysAgo)
        .maybeSingle();

      setDue(!data);
    })().catch(err => console.error('[useCheckInDue] unhandled error:', err));
  }, []);

  return due;
}
